import React, { Component, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import { isNumber } from 'lodash';
import { compose } from 'recompose';
import { connect, useStore } from 'react-redux';

import { DEFAULT_PAGE_SIZE } from '../constants';
import Pagination from '../views/Shared/Pagination';
import { clearPage } from '../data/page/actions';

const withPagination = (
  gqlQuery,
  responseField,
  options = {},
  mountVariablesFromState = null
) => WrappedComponent => {
  class PaginatedWrapper extends Component {
    constructor(props) {
      super(props);
      this.state = {
        page: 1,
        lastPage: false,
        loading: false,
      };
    }

    componentDidUpdate(prevProps) {
      const { pageNo } = this.props;
      if (
        prevProps.pageNo !== pageNo
        && isNumber(pageNo)
      ) {
        this.setState({ page: 1, lastPage: false });
      }
    }

    render() {
      const { page, lastPage, loading } = this.state;
      const { data, clearPage: clearPageProp } = this.props;
      const dataSize = (data[responseField] || []).length;
      const start = dataSize > 0 ? (page - 1) * DEFAULT_PAGE_SIZE + 1 : 0;
      const end = start > 0 ? start + dataSize - 1 : 0;

      const nextHandler = () => {
        if (!lastPage) {
          const offset = end;
          this.setState({ loading: true });
          data.fetchMore({
            variables: {
              size: DEFAULT_PAGE_SIZE,
              offset,
              page: page + 1,
            },
            updateQuery: (
              previousResult,
              { fetchMoreResult }
            ) => {
              clearPageProp({});
              if (fetchMoreResult[responseField].length > 0) {
                if (fetchMoreResult[responseField].length < DEFAULT_PAGE_SIZE) {
                  this.setState({
                    page: page + 1,
                    loading: false,
                    lastPage: true,
                  });
                } else {
                  this.setState({ page: page + 1, loading: false });
                }
                return fetchMoreResult;
              }
              this.setState({ lastPage: true, loading: false });
              return previousResult;
            },
          });
        }
      };

      const previousHandler = () => {
        if (page > 1) {
          this.setState({ loading: true, lastPage: false });
          const offset = (page - 2) * DEFAULT_PAGE_SIZE;
          data.fetchMore({
            variables: {
              size: DEFAULT_PAGE_SIZE,
              offset,
              page: page - 1,
            },
            updateQuery: (
              previousResult,
              { fetchMoreResult }
            ) => {
              clearPageProp({});
              this.setState({
                page: page - 1,
                loading: false,
                lastPage: false,
              });
              return fetchMoreResult;
            },
          });
        }
      };

      return (
        <div>
          {React.createElement(WrappedComponent, this.props)}
          <Pagination
            nextHandler={nextHandler}
            previousHandler={previousHandler}
            start={start}
            end={end}
            loading={loading}
            lastPage={lastPage}
            pageSize={DEFAULT_PAGE_SIZE}
          />
        </div>
      );
    }
  }

  PaginatedWrapper.propTypes = {
    pageNo: PropTypes.number,
    data: PropTypes.shape({
      fetchMore: PropTypes.func.isRequired,
    }).isRequired,
    clearPage: PropTypes.func.isRequired,
  };

  const mapDispatchToProps = dispatch => ({
    clearPage: () => {
      dispatch(clearPage());
    },
  });

  const mapStateToProps = state => {
    const { data: { page } } = state;
    return {
      pageNo: page,
    };
  };

  const queryVars = {
    size: DEFAULT_PAGE_SIZE,
    vin: null,
    dealerNumbers: null,
    dealerNumber: null,
    locationInitial: null,
    auctionAccessNumber: null,
    batchId: null,
    ...options,
  };

  if (!mountVariablesFromState) {
    return compose(
      graphql(gqlQuery, {
        options: {
          fetchPolicy: 'network-only',
          variables: queryVars,
        },
      }),
      connect(mapStateToProps, mapDispatchToProps)
    )(PaginatedWrapper);
  }

  const PaginatedQuery = compose(
    graphql(gqlQuery, {
      options: ({ readStateVariables }) => ({
        fetchPolicy: 'network-only',
        variables: { ...queryVars, ...readStateVariables() },
      }),
    }),
    connect(mapStateToProps, mapDispatchToProps)
  )(PaginatedWrapper);

  const WithMountVariables = props => {
    const store = useStore();
    const [isMounted, setIsMounted] = useState(false);
    // Read after mount so the previous page's reset-on-exit cleanup has already run.
    useEffect(() => {
      setIsMounted(true);
    }, []);
    if (!isMounted) return null;
    const readStateVariables = () => mountVariablesFromState(store.getState());
    return React.createElement(PaginatedQuery, { ...props, readStateVariables });
  };

  return WithMountVariables;
};

export default withPagination;
